import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useSitesStore } from '@/stores/sites';
import { useAnalyticsStore } from '@/stores/analytics';
import { useUsageStore } from '@/stores/usage';
import { Card, CardHeader, Button } from '@/components/UI';
import { MetricCard, PeriodSelector, Sparkline } from '@/components/Dashboard';
import { AddSiteWizard } from '@/components/Sites/AddSiteWizard';

export function OrgDashboard() {
  const { currentOrganization } = useAuthStore();
  const { sites, fetchSites, isLoading: sitesLoading } = useSitesStore();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const {
    organizationOverview,
    fetchOrganizationOverview,
    period,
    setPeriod,
  } = useAnalyticsStore();
  const { usage, fetchUsage } = useUsageStore();

  const planLimit = usage?.limit ?? currentOrganization?.request_limit ?? 5000;

  // Merge sites from store with analytics data from overview
  // This ensures sites show even if ClickHouse is unavailable
  const displaySites = sites.map((site) => {
    const overviewSite = organizationOverview?.sites?.find((s) => s.id === site.id);
    return {
      id: site.id,
      name: site.name,
      domain: site.domain,
      pageviews: overviewSite?.pageviews || 0,
      visitors: overviewSite?.visitors || 0,
      time_series: overviewSite?.time_series || [],
    };
  });

  useEffect(() => {
    if (currentOrganization) {
      fetchSites(currentOrganization.id);
      fetchOrganizationOverview(currentOrganization.id, period);
      fetchUsage(currentOrganization.id);

      // Auto-refresh every 60 seconds
      const interval = setInterval(() => {
        fetchOrganizationOverview(currentOrganization.id, period);
        fetchUsage(currentOrganization.id);
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [currentOrganization, period]);

  if (!currentOrganization) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">No organization selected</h2>
        <p className="mt-2 text-gray-600">Create or select an organization to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">{currentOrganization.name}</p>
        </div>
        <div className="flex items-center gap-4">
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Pageviews"
          value={organizationOverview?.total_pageviews || 0}
          icon={
            <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          }
        />
        <MetricCard
          title="Total Visitors"
          value={organizationOverview?.unique_visitors || 0}
          icon={
            <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
        />
        <MetricCard
          title="Active Sites"
          value={displaySites.length}
          icon={
            <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
          }
        />
        <MetricCard
          title="Plan Usage"
          value={usage ? usage.usage.toLocaleString() : '—'}
          subtitle={`of ${planLimit === -1 ? 'Unlimited' : planLimit.toLocaleString()} this month`}
          icon={
            <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
      </div>

      {/* Sites List */}
      <Card>
        <CardHeader
          title="Your Sites"
          description="Click on a site to view detailed analytics"
          action={
            <Button size="sm" onClick={() => setIsWizardOpen(true)}>Add Site</Button>
          }
        />

        {sitesLoading && sites.length === 0 ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-4 bg-gray-200 rounded w-16" />
              </div>
            ))}
          </div>
        ) : displaySites.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No sites</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first site.</p>
            <div className="mt-6">
              <Button onClick={() => setIsWizardOpen(true)}>Add your first site</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {displaySites.map((site) => (
              <Link
                key={site.id}
                to={`/sites/${site.id}`}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${site.domain}&sz=64`}
                      alt=""
                      className="w-6 h-6"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        (e.currentTarget.nextElementSibling as HTMLElement)?.classList.remove('hidden');
                      }}
                    />
                    <svg className="w-5 h-5 text-gray-400 hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{site.name}</h3>
                    <p className="text-sm text-gray-500">{site.domain}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <Sparkline
                    data={site.time_series?.map((d: { pageviews: number }) => d.pageviews) || []}
                    width={300}
                    height={28}
                  />
                  <div className="text-right min-w-[120px]">
                    <p className="font-medium text-gray-900">
                      {site.pageviews.toLocaleString()} pageviews
                    </p>
                    <p className="text-sm text-gray-500">
                      {site.visitors.toLocaleString()} visitors
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      {/* Add Site Wizard */}
      {currentOrganization && (
        <AddSiteWizard
          isOpen={isWizardOpen}
          onClose={() => setIsWizardOpen(false)}
          onSuccess={() => {
            fetchSites(currentOrganization.id);
            fetchOrganizationOverview(currentOrganization.id, period);
          }}
          organizationId={currentOrganization.id}
        />
      )}
    </div>
  );
}

export default OrgDashboard;
